/**
 * Utility functions for subdomain detection and beta feature checks
 */

/**
 * Check if the current hostname is the vitals subdomain
 *
 * @returns true if on vitals subdomain, false otherwise
 */
export const isVitalsSubdomain = (): boolean => {
  return window.location.hostname === "vitals.circadia.link";
};

/**
 * Check if the current hostname is the Compassus subdomain
 *
 * @returns true if on compassus subdomain, false otherwise
 */
export const isCompassusSubdomain = (): boolean => {
  return (
    typeof window !== "undefined" &&
    (window.location.hostname === "compassus.circadia.link" ||
      window.location.hostname === "compassus.circadia.health")
  );
};

/**
 * VoIP calling is only enabled on staging / beta / local hosts while the
 * feature is being validated. Production hosts are intentionally excluded.
 *
 * @returns true if the current hostname is allowed to use VoIP calling
 */
export const isVoipAllowedHost = (): boolean => {
  if (typeof window === "undefined") return false;
  const allowedHosts = [
    "cipstaging.circadia.link",
    "cip-beta.circadia.link",
    "localhost",
  ];
  return allowedHosts.includes(window.location.hostname);
};

/**
 * Device Orders is a beta-only mock. Keep it available on local hosts for
 * development while preventing it from appearing on staging or production.
 */
export const isDeviceOrdersAllowedHost = (): boolean => {
  if (typeof window === "undefined") return false;
  return [
    "cip-beta.circadia.link",
    "localhost",
    "127.0.0.1",
    "0.0.0.0",
  ].includes(window.location.hostname);
};

/**
 * Check if user has vitals-ai beta feature enabled
 *
 * @param betaFeaturesAllowedList - Array of beta features from Redux state
 * @returns true if "vitals-ai" is in the beta features list, false otherwise
 */
export const hasVitalsAIBetaFeature = (
  betaFeaturesAllowedList?: string[]
): boolean => {
  return (
    Array.isArray(betaFeaturesAllowedList) &&
    betaFeaturesAllowedList.includes("vitals-ai")
  );
};

/**
 * Home-health (Compassus) Navigator — gated by user beta feature from backend.
 */
export const hasCompassusNavigatorBetaFeature = (
  betaFeaturesAllowedList?: string[]
): boolean => {
  return (
    Array.isArray(betaFeaturesAllowedList) &&
    betaFeaturesAllowedList.includes("compassus-navigator")
  );
};

/**
 * Home-health (Compassus) NP Productivity dashboard — gated by user beta
 * feature from backend.
 */
export const hasCompassusProductivityBetaFeature = (
  betaFeaturesAllowedList?: string[]
): boolean => {
  return (
    Array.isArray(betaFeaturesAllowedList) &&
    betaFeaturesAllowedList.includes("compassus-productivity")
  );
};

/**
 * Home-health (Compassus) NP self-service dashboard — gated by user beta
 * feature from backend.
 */
export const hasCompassusNpDashboardBetaFeature = (
  betaFeaturesAllowedList?: string[]
): boolean => {
  return (
    Array.isArray(betaFeaturesAllowedList) &&
    betaFeaturesAllowedList.includes("compassus-np-dashboard")
  );
};

/**
 * Project Neo (conversational AI agent) beta access.
 *
 * Returns true if ANY of the following match:
 *   - Hostname is ``localhost``.
 *   - User's email ends with ``@circadia.health`` AND does NOT start
 *     with ``pcc_`` (see warning below about PCC SSO synthetic emails).
 *   - ``betaFeaturesAllowedList`` includes any of
 *     {@link NEO_ACCESS_GRANTING_FEATURES}.
 *
 * Self-contained (no external deps). Safe to call from the router
 * before any React tree has mounted.
 */
// ---------------------------------------------------------------------------
// CIRCADIA SSO PRIVILEGE-ESCALATION BUG -- spot-fix 2026-05-07
//
// Why the `!startsWith("pcc_")` guard exists:
//
// PCC SSO users (PointClickCare clinicians authenticating from outside the
// company) don't necessarily have a real email address. At some point a
// developer working on auth thought "users without an email need an email,
// I'll synthesize one" and started assigning them fictitious addresses of
// the shape `pcc_<something>@circadia.health`.
//
// Independently, other developers across the codebase wrote checks of the
// form `email.endsWith("@circadia.health")` assuming that domain is owned
// by the company and therefore anyone with such an email must be a Circadia
// employee.
//
// PCC SSO is not the bug. Each individual decision was reasonable in
// isolation. The bug is emergent: combining "synthesize fake emails on our
// own domain for users who don't have one" with "treat the corporate domain
// suffix as a proxy for employment" silently grants every external PCC SSO
// user every privilege we gate on `@circadia.health`.
//
// THE PROPER FIX is to mint these synthetic addresses on a separate,
// unprivileged domain so the privileged employee domain stays disjoint
// from any user-facing one, and then delete every `startsWith("pcc_")`
// patch like this one. Facebook did exactly this circa 2011: employees
// were issued `@fb.com` addresses (Facebook bought fb.com in 2010 for
// ~$8.5M precisely so the employee domain could be short and disjoint),
// while end-user Messenger/profile addresses lived on `@facebook.com`.
// Our equivalent would be something like `pcc_*@pcc-sso.circadia.invalid`
// (or any non-corporate domain we control) for the synthesized PCC
// addresses, leaving `@circadia.health` to mean "employee" again.
//
// Until that refactor lands, this is patched in TWO places only:
//   1. hasProjectNeoAccess              (cip-app/src/util/subdomain.ts)
//   2. shouldShowNpSchedulingSidebarLink (cip-app/src/util/common.ts)
//
// All of these other call sites STILL treat any `@circadia.health` email
// as internal/privileged and remain VULNERABLE to PCC SSO users:
//   - cip-app/src/util/devicelessRouting.ts
//       Auto-rewrites inbox/flag/alert API endpoints to their `-deviceless`
//       variants for clinics with no device installed.
//   - cip-app/src/pages/Settings/FacilityInformation/index.tsx
//       Enables editing the Facility Information textarea.
//   - cip-app/src/pages/VitalSync/useHook.tsx
//       Suppresses Mixpanel analytics events ("test user" filter).
//   - Patient-Centric-App-V2/app/crud/mysql/Alerts.py
//       Sets `flag_ack_by_is_internal = 1` on PSN flag acknowledgements.
//   - Patient-Centric-App-V2/app/crud/mysql/PSNV2_Flags.py
//       Excludes the user from the top-acknowledgers leaderboard.
//   - Patient-Centric-App-V2/app/controller/User.py
//       Excludes the user from external admission-team / general-user
//       email recipient splits.
//   - Patient-Centric-App-V2/app/controller/CronJobs/weekly_concise_summary_email.py
//   - Patient-Centric-App-V2/app/controller/CronJobs/weekly_concise_summary_email_v2.py
//       Exclude the user from external alerts / daily-report recipient lists.
// ---------------------------------------------------------------------------
/**
 * Internal Circadia employee proxy: a real corporate `@circadia.health` inbox,
 * excluding the synthetic `pcc_*@circadia.health` addresses minted for PCC SSO
 * users (see the privilege-escalation note above). Email-only (no localhost
 * short-circuit) so callers can gate employee-only defaults (e.g. landing page)
 * without dev machines being treated as employees.
 */
export const isInternalCircadiaUser = (email?: string | null): boolean => {
  const normalizedEmail = (email ?? "").trim().toLowerCase();
  return (
    normalizedEmail.endsWith("@circadia.health") &&
    !normalizedEmail.startsWith("pcc_")
  );
};

/**
 * Beta features that grant base Neo access.
 *
 * ``project-neo-pro`` is a tier ABOVE ``project-neo`` (it additionally unlocks
 * the Agents surface, gated separately in ``pages/Neo/index.tsx``), so holding
 * it implies base access. The implication is strictly one-way: base access must
 * never confer Pro. Any future tier belongs in this list too — a tier missing
 * here locks its holders out of Neo entirely rather than merely denying them
 * the extra surface.
 */
const NEO_ACCESS_GRANTING_FEATURES: readonly string[] = [
  "project-neo",
  "project-neo-pro",
];

export const hasProjectNeoAccess = (args: {
  email?: string | null;
  betaFeaturesAllowedList?: string[];
}): boolean => {
  if (
    typeof window !== "undefined" &&
    window.location.hostname === "localhost"
  ) {
    return true;
  }
  if (isInternalCircadiaUser(args.email)) {
    return true;
  }
  if (
    Array.isArray(args.betaFeaturesAllowedList) &&
    args.betaFeaturesAllowedList.some((feature) =>
      NEO_ACCESS_GRANTING_FEATURES.includes(feature)
    )
  ) {
    return true;
  }
  return false;
};

/**
 * Intercom (VoIP calling directory) beta access.
 *
 * Mirrors {@link hasProjectNeoAccess}. Returns true if ANY of:
 *   - Hostname is ``localhost``.
 *   - User's email ends with ``@circadia.health`` AND does NOT start with
 *     ``pcc_`` (see the PCC SSO privilege-escalation note above — synthetic
 *     PCC emails on our corporate domain must not be treated as internal).
 *   - ``betaFeaturesAllowedList`` includes ``"intercom"``.
 *
 * When true, the Intercom sales page is replaced with the live calling
 * directory. Self-contained; safe to call before the React tree mounts.
 */
export const hasIntercomAccess = (args: {
  email?: string | null;
  betaFeaturesAllowedList?: string[];
}): boolean => {
  if (
    typeof window !== "undefined" &&
    window.location.hostname === "localhost"
  ) {
    return true;
  }
  const normalizedEmail = (args.email ?? "").trim().toLowerCase();
  if (
    normalizedEmail.endsWith("@circadia.health") &&
    !normalizedEmail.startsWith("pcc_")
  ) {
    return true;
  }
  if (
    Array.isArray(args.betaFeaturesAllowedList) &&
    args.betaFeaturesAllowedList.includes("intercom")
  ) {
    return true;
  }
  return false;
};

const HOMEHEALTH_NEO_ACTIVATED_CLINIC_IDS = new Set([
  10706, // CC1418 - OC ANAHEIM CA (Compassus HomeHealth)
  10707, // CC1419 - OC MISSION VIEJO CA (Compassus HomeHealth)
  10709, // CC1421 - OC SOUTH CA (Compassus HomeHealth)
  10588, // R07 - LA County CA (Compassus Palliative)
  10587, // P31 - Columbia TN (Compassus Palliative)
]);

export const homehealthClinicHasNeoActivated = (
  clinicId?: number | string | null
): boolean => {
  const normalizedClinicId = Number(clinicId);
  return (
    Number.isInteger(normalizedClinicId) &&
    HOMEHEALTH_NEO_ACTIVATED_CLINIC_IDS.has(normalizedClinicId)
  );
};
